/**
 * Platform admin card for the Stripe webhook inbox (BILL-17, CLAUDE.md §3.6).
 *
 * Wires the super-admin endpoints in `billing_operations.py`:
 *   GET  /api/billing/admin/webhook-events
 *   POST /api/billing/admin/webhook-events/{stripe_event_id}/retry
 *   POST /api/billing/admin/reconcile
 * Shows event metadata only (type, status, attempts, timestamps, Stripe event
 * id, truncated last error). Never renders event payloads.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { AdminSectionCard, AdminStatusPill, ADMIN_COLORS } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import { confirmDialog } from '../../utils/dialogs';
import {
  mapInboxEvents,
  webhookStatusLabelKey,
  type InboxFilter,
  type InboxRow,
  type WebhookEventsResponse,
} from '../../utils/stripeWebhookInbox';

const LIST_LIMIT = 100;

function formatDateTime(value: Date | null, language: string): string {
  if (!value) return '—';
  return value.toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function StripeWebhookInboxCard() {
  const { t, language } = useTranslation();
  const { showToast } = useToast();
  const [response, setResponse] = useState<WebhookEventsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState<InboxFilter>('attention');
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await apiRequest<WebhookEventsResponse>({
        endpoint: '/billing/admin/webhook-events',
        params: { limit: LIST_LIMIT },
      });
      setResponse(data);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => mapInboxEvents(response, filter), [response, filter]);

  const handleRetry = async (row: InboxRow) => {
    const confirmed = await confirmDialog(
      t('platformAdmin.webhookInbox.retryConfirmTitle'),
      t('platformAdmin.webhookInbox.retryConfirmMessage', { eventType: row.eventType, eventId: row.id }),
      t('platformAdmin.webhookInbox.retryAction'),
      t('common.cancel'),
    );
    if (!confirmed) return;
    setRetryingId(row.id);
    try {
      await apiRequest({
        endpoint: `/billing/admin/webhook-events/${encodeURIComponent(row.id)}/retry`,
        method: 'POST',
      });
      showToast(t('platformAdmin.webhookInbox.retrySuccess'), 'success');
      await load();
    } catch {
      showToast(t('platformAdmin.webhookInbox.retryError'), 'error');
    } finally {
      setRetryingId(null);
    }
  };

  const handleReconcile = async () => {
    const confirmed = await confirmDialog(
      t('platformAdmin.webhookInbox.reconcileConfirmTitle'),
      t('platformAdmin.webhookInbox.reconcileConfirmMessage'),
      t('platformAdmin.webhookInbox.reconcileAction'),
      t('common.cancel'),
    );
    if (!confirmed) return;
    setReconciling(true);
    try {
      await apiRequest({ endpoint: '/billing/admin/reconcile', method: 'POST' });
      showToast(t('platformAdmin.webhookInbox.reconcileSuccess'), 'success');
    } catch {
      showToast(t('platformAdmin.webhookInbox.reconcileError'), 'error');
    } finally {
      setReconciling(false);
    }
  };

  const statusLabel = (status: string) => {
    const key = webhookStatusLabelKey(status);
    return key ? t(key) : status;
  };

  const filterOptions: { key: InboxFilter; label: string }[] = [
    { key: 'attention', label: t('platformAdmin.webhookInbox.filterAttention') },
    { key: 'all', label: t('platformAdmin.webhookInbox.filterAll') },
  ];

  return (
    <AdminSectionCard
      icon="inbox"
      title={t('platformAdmin.webhookInbox.title')}
      subtitle={t('platformAdmin.webhookInbox.description')}
    >
      <View className="flex-row flex-wrap items-center" style={{ gap: 8 }}>
        {filterOptions.map((option) => {
          const active = filter === option.key;
          return (
            <TouchableOpacity
              key={option.key}
              onPress={() => setFilter(option.key)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              style={{
                borderWidth: 1,
                borderColor: active ? ADMIN_COLORS.primary : ADMIN_COLORS.border,
                backgroundColor: active ? ADMIN_COLORS.primaryFaint : ADMIN_COLORS.background,
                borderRadius: 999,
                paddingHorizontal: 12,
                paddingVertical: 6,
              }}
            >
              <Text className="text-xs font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
                {option.label}
              </Text>
            </TouchableOpacity>
          );
        })}
        <View style={{ flexGrow: 1 }} />
        <Button variant="outline" size="sm" onPress={() => void load()} disabled={loading}>
          <Text className="text-xs font-semibold">{t('platformAdmin.webhookInbox.refresh')}</Text>
        </Button>
        <Button size="sm" onPress={() => void handleReconcile()} disabled={reconciling}>
          <Text className="text-xs font-semibold text-primary-foreground">
            {reconciling ? t('common.loading') : t('platformAdmin.webhookInbox.reconcileAction')}
          </Text>
        </Button>
      </View>

      {loading && !response ? (
        <ActivityIndicator size="small" color={ADMIN_COLORS.primary} />
      ) : loadFailed ? (
        <Text className="text-sm" style={{ color: ADMIN_COLORS.errorText }}>
          {t('platformAdmin.webhookInbox.loadError')}
        </Text>
      ) : rows.length === 0 ? (
        <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
          {filter === 'attention'
            ? t('platformAdmin.webhookInbox.emptyAttention')
            : t('platformAdmin.webhookInbox.emptyAll')}
        </Text>
      ) : (
        <View style={{ gap: 10 }}>
          {rows.map((row) => (
            <View
              key={row.id}
              testID={`webhook-event-${row.id}`}
              style={{
                borderWidth: 1,
                borderColor: ADMIN_COLORS.border,
                borderRadius: 12,
                padding: 12,
                gap: 4,
              }}
            >
              <View className="flex-row items-center flex-wrap" style={{ gap: 8 }}>
                <Text className="text-sm font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
                  {row.eventType}
                </Text>
                <AdminStatusPill label={statusLabel(row.status)} tone={row.statusTone} />
              </View>
              <Text className="text-xs" style={{ color: ADMIN_COLORS.text.secondary }} selectable>
                {row.id}
              </Text>
              <Text className="text-xs" style={{ color: ADMIN_COLORS.text.secondary }}>
                {t('platformAdmin.webhookInbox.meta', {
                  attempts: row.attempts,
                  received: formatDateTime(row.receivedAt, language),
                  next: formatDateTime(row.nextAttemptAt, language),
                })}
              </Text>
              {row.errorSnippet ? (
                <Text className="text-xs" style={{ color: ADMIN_COLORS.errorText }}>
                  {t('platformAdmin.webhookInbox.lastError', { error: row.errorSnippet })}
                </Text>
              ) : null}
              {row.canRetry ? (
                <View className="flex-row" style={{ marginTop: 4 }}>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={retryingId !== null}
                    onPress={() => void handleRetry(row)}
                  >
                    <Text className="text-xs font-semibold">
                      {retryingId === row.id ? t('common.loading') : t('platformAdmin.webhookInbox.retryAction')}
                    </Text>
                  </Button>
                </View>
              ) : null}
            </View>
          ))}
        </View>
      )}
    </AdminSectionCard>
  );
}
